import type React from 'react'

import { ProviderInNetworkVerificationTakeover } from './components/ProviderInNetworkVerificationTakeover'
import {
  getStoredHistory,
  selectedPlansFromSmartBudget,
} from './navigationUtils'
import { AdditionalDetailsPage } from './pages/AdditionalDetailsPage'
import { ConfirmAndEnrollPage } from './pages/ConfirmAndEnrollPage'
import { DentalPlansPage } from './pages/DentalPlansPage'
import { GenerateAllowancesPage } from './pages/GenerateAllowancesPage'
import {
  MedicalPlanDetailPage,
  RenewalCurrMedicalPlanDetailPage,
  RenewalNextMedicalPlanDetailPage,
} from './pages/MedicalPlanDetailPage'
import { MedicalPlansComparePage } from './pages/MedicalPlansComparePage'
import { MedicalPlansPageRouter } from './pages/MedicalPlansPageRouter'
import {
  OptedOutFromSubsidyEligiblePage,
  OptedOutPage,
} from './pages/OptedOutPage'
import { PhoneVerificationPage } from './pages/PhoneVerificationPage'
import QleDetailsPage from './pages/QleDetailsPage'
import QleDocumentUploadPage from './pages/QleDocumentUploadPage'
import { RenewalConfirmInfoPage } from './pages/RenewalConfirmInfoPage'
import { RenewalShowSummaryPage } from './pages/RenewalShowSummaryPage'
import { SmartBudgetPage } from './pages/SmartBudgetPage'
import { StateFormPage } from './pages/StateFormPage'
import { SubsidyEligiblePage } from './pages/SubsidyEligiblePage'
import { ViewAllowancesPage } from './pages/ViewAllowancesPage'
import { VisionPlansPage } from './pages/VisionPlansPage'
import {
  type OnboardingInfo,
  type Step,
  type StepComponentProps,
} from './types'
import { allowanceIsEmpty, isNewQleFlow, isRenewal, isRenewalOrQleFlow, shouldSkipQleDocumentUpload } from './utils'

export type FeatureFlags = { [key: string]: boolean | undefined }

/** For SEP with V2 on: QLE details step is completed when both subtype and provided_qle_event_date are set. */
const qleDetailsCompleted = (data: OnboardingInfo): boolean => {
  const event = data.enrollment_event
  return !!event?.subtype && !!event?.provided_qle_event_date
}

/** When true, step can be shown. Blocks all QLE flow steps (except qle_details) until subtype and provided_qle_event_date are set. */
const qleDetailsRequiredToShowStep = (data: OnboardingInfo, featureFlags?: FeatureFlags): boolean =>
  !isNewQleFlow(data, featureFlags?.newQleEnabled) || qleDetailsCompleted(data)

const phoneNumberNeedsToBeVerified = (data: OnboardingInfo) => {
  return (
    data.restrictions?.phone_number_verification ===
    'phone_number_verification_required'
  )
}
const phoneNumberVerified = (data: OnboardingInfo) => {
  return !data.restrictions?.phone_number_verification
}
const memberAllowanceMissingFieldsAreEmpty = (data: OnboardingInfo) => {
  const allowanceFields = data.missing_fields.allowance
  if (!allowanceFields || allowanceFields.length === 0) return true
  return allowanceFields.every((field) => {
    return !field.startsWith('member.')
  })
}
const participantsAllowanceMissingFieldsAreEmpty = (data: OnboardingInfo) => {
  const allowanceFields = data.missing_fields.allowance
  if (!allowanceFields || allowanceFields.length === 0) return true
  return allowanceFields.every((field) => {
    return !field.startsWith('additional_participants.')
  })
}
const enrollmentMissingFieldsAreEmpty = (data: OnboardingInfo) => {
  const enrollmentFields = data.missing_fields.enrollment
  if (!enrollmentFields || enrollmentFields.length === 0) return true
  return false
}
const memberIsMissingStateEnrollmentFields = (data: OnboardingInfo) => {
  const enrollmentFields = data.missing_fields.enrollment
  if (!enrollmentFields || enrollmentFields.length === 0) return false
  return enrollmentFields.some((field) => {
    return field === 'state_config'
  })
}
const dentalPlanSelected = (data: OnboardingInfo) => {
  const enrollments = data.enrollments
  if (!enrollments || !enrollments.length) return false
  return (
    enrollments.filter((enrollment) => enrollment.covers_dental).length > 0
  )
}
const visionPlanSelected = (data: OnboardingInfo) => {
  const enrollments = data.enrollments
  if (!enrollments || !enrollments.length) return false
  return (
    enrollments.filter((enrollment) => enrollment.covers_vision).length > 0
  )
}
const medicalPlanSelected = (data: OnboardingInfo) => {
  const enrollments = data.enrollments
  if (!enrollments || !enrollments.length) return false

  const medicalEnrollments = enrollments.filter(
    (enrollment) => enrollment.covers_medical
  )
  if (medicalEnrollments.length === 0) return false

  if (
    medicalEnrollments.some(
      (enrollment) => enrollment.payment_method !== 'paid_by_thatch'
    )
  ) {
    // if the medical enrollment is off thatch then the POC should have been uploaded
    return !data.missing_fields.enrollment?.includes(
      'off_thatch_proof_of_coverage'
    )
  } else {
    return true
  }
}
const proofOfQleDocumentsUploaded = (data: OnboardingInfo) => {
  return !!data.member && !data.missing_fields.enrollment?.includes('proof_of_qle')
}

const proofOfQleDocumentsStepCompletedIfQLEFlow = (
  data: OnboardingInfo,
  featureFlags?: FeatureFlags
) => {
  // If QLE document upload is not required for this flow, consider the step completed
  if (!isNewQleFlow(data, featureFlags?.newQleEnabled)) {
    return true
  }

  if (shouldSkipQleDocumentUpload(data.enrollment_event)) {
    return true
  }

  return proofOfQleDocumentsUploaded(data)
}

const failedToGenerateAllowance = (data: OnboardingInfo) => {
  return data.restrictions?.allowance_generation_failed
}
const additionalDetailsMissing = (data: OnboardingInfo) => {
  return (
    data.missing_fields.plans_selection &&
    data.missing_fields.plans_selection.length > 0
  )
}
const eligibleForSubsidy = (data: OnboardingInfo) => {
  return (
    !additionalDetailsMissing(data) &&
    !allowanceIsEmpty(data.allowance) &&
    !!data.restrictions?.subsidy_eligible
  )
}
const memberMissingCounty = (data: OnboardingInfo) => {
  return data.restrictions?.member_missing_county
}
const stepAfterMedicalPlan = (data: OnboardingInfo | null | undefined, featureFlags?: FeatureFlags) => {
  if (data && !data?.restrictions?.dental_not_available) {
    return 'dental_plan'
  }
  if (data && !data?.restrictions?.vision_not_available) {
    return 'vision_plan'
  }
  if (data?.state_config) {
    return 'state_form'
  }
  if (data && isNewQleFlow(data, featureFlags?.newQleEnabled)) {
    if (shouldSkipQleDocumentUpload(data.enrollment_event)) {
      return 'renewal_confirm_enrollment'
    }
    return 'qle_document_upload'
  }
  if (data && isRenewal(data)) {
    return 'renewal_confirm_enrollment'
  }
  return 'finalize_enrollment'
}
const stepAfterDentalPlan = (data: OnboardingInfo | null | undefined, featureFlags?: FeatureFlags) => {
  if (data && !data?.restrictions?.vision_not_available) {
    return 'vision_plan'
  }
  if (data?.state_config) {
    return 'state_form'
  }
  if (data && isNewQleFlow(data, featureFlags?.newQleEnabled)) {
    if (shouldSkipQleDocumentUpload(data.enrollment_event)) {
      return 'renewal_confirm_enrollment'
    }
    return 'qle_document_upload'
  }
  if (data && isRenewal(data)) {
    return 'renewal_confirm_enrollment'
  }
  return 'finalize_enrollment'
}
const stepAfterVisionPlan = (data: OnboardingInfo | null | undefined, featureFlags?: FeatureFlags) => {
  if (data?.state_config) {
    return 'state_form'
  }
  if (data && isNewQleFlow(data, featureFlags?.newQleEnabled)) {
    if (shouldSkipQleDocumentUpload(data.enrollment_event)) {
      return 'renewal_confirm_enrollment'
    }
    return 'qle_document_upload'
  }
  if (data && isRenewal(data)) {
    return 'renewal_confirm_enrollment'
  }
  return 'finalize_enrollment'
}
const stepBeforeVisionPlan = (data: OnboardingInfo | null | undefined) => {
  if (data && !data?.restrictions?.dental_not_available) {
    return 'dental_plan'
  }
  return 'medical_plan'
}
const lastPlanSelectionStep = (
  data: OnboardingInfo | null | undefined,
  enrollmentEventId?: string
) => {
  // Check if user came from smart_budget
  if (selectedPlansFromSmartBudget(enrollmentEventId)) {
    return 'smart_budget'
  }

  // Default logic for traditional flow
  if (data && !data?.restrictions?.vision_not_available) {
    return 'vision_plan'
  } else if (data && !data?.restrictions?.dental_not_available) {
    return 'dental_plan'
  }
  return 'medical_plan'
}
const stepBeforeFinalizeEnrollment = (
  data: OnboardingInfo | null | undefined,
  enrollmentEventId?: string,
  featureFlags?: FeatureFlags
) => {
  if (data?.state_config) {
    return 'state_form'
  }
  if (data && isNewQleFlow(data, featureFlags?.newQleEnabled)) {
    if (shouldSkipQleDocumentUpload(data.enrollment_event)) {
      // Skip document upload, go back to last plan selection step
      return lastPlanSelectionStep(data, enrollmentEventId)
    }
    return 'qle_document_upload'
  }
  return lastPlanSelectionStep(data, enrollmentEventId)
}
const hasCompletedPlanSelectionPrerequisites = (data: OnboardingInfo) => {
  return (
    memberAllowanceMissingFieldsAreEmpty(data) &&
    participantsAllowanceMissingFieldsAreEmpty(data) &&
    !allowanceIsEmpty(data.allowance) &&
    phoneNumberVerified(data) &&
    !additionalDetailsMissing(data) &&
    !memberMissingCounty(data)
  )
}
const hasSelectedAllPlans = (data: OnboardingInfo) => {
  if ((data.missing_fields.enrollment || []).includes('medical_enrollment')) {
    return false
  }

  // Note: currently we are not producing any enrollments for medicare, so we can assume that the
  // condition above would have filtered out missing required medical enrollment cases
  const medicalEnrollments = data.enrollments?.filter(
    (enrollment) => enrollment.covers_medical
  )
  if (
    medicalEnrollments?.some(
      (enrollment) => enrollment.payment_method !== 'paid_by_thatch'
    )
  ) {
    // if the medical enrollment is off thatch then the POC should have been uploaded
    return !data.missing_fields.enrollment?.includes(
      'off_thatch_proof_of_coverage'
    )
  }
  return true
}

/* Logic needed to get the step based on the data returned */
export const getStepFromData = (data: OnboardingInfo, featureFlags?: FeatureFlags): Step | null => {
  const eligibleForSubsidyResult = eligibleForSubsidy(data)
  // Handle opted out case first
  if (data.progress === 'opted_out') {
    if (eligibleForSubsidyResult) {
      return 'opted_out_from_subsidy_eligible'
    }
    return 'opted_out'
  } else if (data.progress === 'completed') {
    return 'completed'
  } else if (data.progress === 'incomplete') {
    const isUsingRenewalFlow = isRenewalOrQleFlow(data, featureFlags?.newQleEnabled)
    const allowanceIsEmptyResult = allowanceIsEmpty(data.allowance)

    // If this is renewal or QLE flow, we always start with the confirm info page
    // For SEP with V2 on, redirect to qle_details if subtype or qle event date is not set
    if (isUsingRenewalFlow) {
      if (isNewQleFlow(data, featureFlags?.newQleEnabled) && !qleDetailsCompleted(data)) {
        return 'qle_details'
      }
      return 'renewal_confirm_info'
    }

    // Check if we need to generate allowances or verify phone
    if (
      (allowanceIsEmptyResult &&
        (data.missing_fields.allowance?.length ||
          failedToGenerateAllowance(data))) ||
      phoneNumberNeedsToBeVerified(data) ||
      memberMissingCounty(data)
    ) {
      return 'generate_allowances'
    }

    // Check if we have completed all allowance prerequisites
    const hasCompletedAllowancePrerequisites =
      memberAllowanceMissingFieldsAreEmpty(data) &&
      participantsAllowanceMissingFieldsAreEmpty(data) &&
      !allowanceIsEmptyResult &&
      phoneNumberVerified(data) &&
      !memberMissingCounty(data)

    if (!hasCompletedAllowancePrerequisites) {
      // If we haven't completed all prerequisites but have basic allowance info, show view_allowances
      if (
        memberAllowanceMissingFieldsAreEmpty(data) &&
        !allowanceIsEmptyResult &&
        phoneNumberVerified(data) &&
        !memberMissingCounty(data)
      ) {
        return 'view_allowances'
      }
      // If we don't have basic allowance info, we need to generate allowances
      return 'generate_allowances'
    }

    // At this point we know all allowance prerequisites are met
    if (additionalDetailsMissing(data)) {
      return 'additional_details'
    }

    if (eligibleForSubsidyResult) {
      return 'subsidy_eligible'
    }

    // Handle plan selection flow
    if (!medicalPlanSelected(data)) {
      return 'medical_plan'
    }

    if (!data.restrictions?.dental_not_available && !dentalPlanSelected(data)) {
      return 'dental_plan'
    }

    if (!data.restrictions?.vision_not_available && !visionPlanSelected(data)) {
      return 'vision_plan'
    }

    if (data.state_config && memberIsMissingStateEnrollmentFields(data)) {
      return 'state_form'
    }

    // if the member passed all the checks above, we can assume they are ready to finalize their enrollment
    // regardless of having missing enrollment fields or not
    return 'finalize_enrollment'
  }

  return null
}

const extractPlanIds = (step: string): string[] | null => {
  if (
    step.startsWith('medical_plan_details:') ||
    step.startsWith('medical_plan_comparison:') ||
    step.startsWith('in_network_verification:') ||
    step.startsWith('renewal_curr_year_medical_plan_details:') ||
    step.startsWith('renewal_next_year_medical_plan_details:')
  ) {
    const returnValue = step.split(':')[1].split('?')[0].split(',')
    if (returnValue.length === 1 && returnValue[0] === '') {
      return null
    }
    return returnValue
  }
  return null
}

export const getStepMetadata = (
  step: Step | null
): Record<string, any> | undefined => {
  if (!step) return undefined
  if (typeof step === 'string' && step.startsWith('medical_plan_details:')) {
    return { planIds: extractPlanIds(step), openInNetworkVerification: step.includes('open_in_network_verification=true') }
  }
  if (typeof step === 'string' && step.startsWith('in_network_verification:')) {
    return { planIds: extractPlanIds(step) }
  }
  if (typeof step === 'string' && step.startsWith('medical_plan_comparison:')) {
    return { planIds: extractPlanIds(step) }
  }
  if (
    typeof step === 'string' &&
    step.startsWith('renewal_curr_year_medical_plan_details:')
  ) {
    return { planIds: extractPlanIds(step) }
  }
  if (
    typeof step === 'string' &&
    step.startsWith('renewal_next_year_medical_plan_details:')
  ) {
    return { planIds: extractPlanIds(step) }
  }
  return undefined
}

export const getBaseStep = (step: Step): Step => {
  if (typeof step === 'string' && step.startsWith('medical_plan_details:')) {
    return 'medical_plan_details'
  }
  if (typeof step === 'string' && step.startsWith('medical_plan_comparison:')) {
    return 'medical_plan_comparison'
  }
  if (typeof step === 'string' && step.startsWith('in_network_verification:')) {
    return 'in_network_verification'
  }
  if (
    typeof step === 'string' &&
    step.startsWith('renewal_curr_year_medical_plan_details:')
  ) {
    return 'renewal_curr_year_medical_plan_details'
  }
  if (
    typeof step === 'string' &&
    step.startsWith('renewal_next_year_medical_plan_details:')
  ) {
    return 'renewal_next_year_medical_plan_details'
  }
  return step
}

export const componentMappings: Partial<
  Record<
    Step,
    {
      component: (props: StepComponentProps) => React.JSX.Element;
      canShow: (
        data: OnboardingInfo,
        metadata?: Record<string, any>,
        featureFlags?: FeatureFlags
      ) => boolean;
      isCompleted?: (data: OnboardingInfo, featureFlags?: FeatureFlags) => boolean;
      previousStep: (
        data?: OnboardingInfo | null,
        enrollmentEventId?: string,
        featureFlags?: FeatureFlags
      ) => Step | null;
      nextStep: (data?: OnboardingInfo | null, featureFlags?: FeatureFlags) => Step | null;
      layout: 'full' | 'no_sidebar' | 'sidebar';
    }
  >
> = {
  qle_details: {
    component: QleDetailsPage,
    canShow: (data, _metadata, featureFlags) => isNewQleFlow(data, featureFlags?.newQleEnabled),
    isCompleted: (data, featureFlags) =>
      !isNewQleFlow(data, featureFlags?.newQleEnabled) || qleDetailsCompleted(data),
    previousStep: () => null, // First page for QLE (though we skip to renewal_confirm_info)
    nextStep: (data, _featureFlags) => {
      // If allowance needs to be generated, go to generate_allowances
      if (data && allowanceIsEmpty(data.allowance)) {
        return 'generate_allowances'
      }
      // Otherwise, go directly to renewal_confirm_info
      return 'renewal_confirm_info'
    },
    layout: 'no_sidebar',
  },
  generate_allowances: {
    component: GenerateAllowancesPage,
    canShow: (data, _metadata, featureFlags) =>
      !isRenewal(data) && qleDetailsRequiredToShowStep(data, featureFlags),
    isCompleted: (data) => memberAllowanceMissingFieldsAreEmpty(data),
    previousStep: (data, _enrollmentEventId, featureFlags) => {
      if (data && isNewQleFlow(data, featureFlags?.newQleEnabled)) {
        return 'qle_details'
      }
      return null
    },
    nextStep: (data, featureFlags) => {
      if (data && phoneNumberNeedsToBeVerified(data)) {
        return 'phone_verification'
      }
      if (data && isNewQleFlow(data, featureFlags?.newQleEnabled)) {
        return 'renewal_confirm_info'
      }
      return 'view_allowances'
    },
    layout: 'no_sidebar',
  },
  phone_verification: {
    component: PhoneVerificationPage,
    canShow: (data, _metadata, featureFlags) =>
      memberAllowanceMissingFieldsAreEmpty(data) &&
      phoneNumberNeedsToBeVerified(data) &&
      !failedToGenerateAllowance(data) &&
      !memberMissingCounty(data) &&
      qleDetailsRequiredToShowStep(data, featureFlags),
    previousStep: (data, _enrollmentEventId, featureFlags) =>
      data && isRenewalOrQleFlow(data, featureFlags?.newQleEnabled) ? 'renewal_confirm_info' : 'generate_allowances',
    nextStep: (data, featureFlags) =>
      data && isRenewalOrQleFlow(data, featureFlags?.newQleEnabled) ? 'renewal_show_summary' : 'view_allowances',
    layout: 'no_sidebar',
  },
  view_allowances: {
    component: ViewAllowancesPage,
    canShow: (data, _metadata, featureFlags) =>
      memberAllowanceMissingFieldsAreEmpty(data) &&
      !allowanceIsEmpty(data.allowance) &&
      phoneNumberVerified(data) &&
      !memberMissingCounty(data) &&
      !isRenewalOrQleFlow(data, featureFlags?.newQleEnabled),
    previousStep: () => 'generate_allowances',
    nextStep: (data) => {
      if (
        data?.restrictions?.medicare_eligible &&
        data.restrictions?.medicare_eligible?.length > 0
      ) {
        return 'medical_plan'
      }
      return 'additional_details'
    },
    layout: 'no_sidebar',
  },
  additional_details: {
    component: AdditionalDetailsPage,
    canShow: (data, _metadata, featureFlags) =>
      memberAllowanceMissingFieldsAreEmpty(data) &&
      participantsAllowanceMissingFieldsAreEmpty(data) &&
      !allowanceIsEmpty(data.allowance) &&
      phoneNumberVerified(data) &&
      !memberMissingCounty(data) &&
      !data.restrictions?.medicare_eligible &&
      !isRenewalOrQleFlow(data, featureFlags?.newQleEnabled),
    isCompleted: (data) => data.missing_fields.plans_selection?.length === 0,
    previousStep: () => 'view_allowances',
    nextStep: (data) => {
      if (data && eligibleForSubsidy(data)) {
        return 'subsidy_eligible'
      }
      return 'medical_plan'
    },
    layout: 'sidebar',
  },
  medical_plan: {
    component: MedicalPlansPageRouter,
    canShow: (data, _metadata, featureFlags) =>
      !allowanceIsEmpty(data.allowance) &&
      data.missing_fields.plans_selection?.length === 0 &&
      qleDetailsRequiredToShowStep(data, featureFlags),
    isCompleted: (data, featureFlags) =>
      medicalPlanSelected(data) ||
      (data.missing_fields.plans_selection?.length === 0 &&
        !isRenewalOrQleFlow(data, featureFlags?.newQleEnabled) &&
        !!data.restrictions?.medicare_eligible),
    previousStep: (data, _enrollmentEventId, featureFlags) =>
      data && isRenewalOrQleFlow(data, featureFlags?.newQleEnabled) ? 'renewal_show_summary' : 'additional_details',
    nextStep: (data, featureFlags) => stepAfterMedicalPlan(data, featureFlags),
    layout: 'sidebar',
  },
  medical_plan_details: {
    component: MedicalPlanDetailPage,
    canShow: (data, metadata, featureFlags) => {
      return (
        data.missing_fields.plans_selection?.length === 0 &&
        metadata?.planIds?.length > 0 &&
        qleDetailsRequiredToShowStep(data, featureFlags)
      )
    },
    previousStep: () => 'medical_plan',
    nextStep: (data, featureFlags) => stepAfterMedicalPlan(data, featureFlags),
    layout: 'no_sidebar',
  },
  in_network_verification: {
    component: ProviderInNetworkVerificationTakeover,
    canShow: (data, _metadata, featureFlags) =>
      !allowanceIsEmpty(data.allowance) &&
      data.missing_fields.plans_selection?.length === 0 &&
      qleDetailsRequiredToShowStep(data, featureFlags),
    previousStep: () => 'medical_plan',
    nextStep: () => 'medical_plan',
    layout: 'full',
  },
  medical_plan_comparison: {
    component: MedicalPlansComparePage,
    canShow: (data, metadata, featureFlags) => {
      return (
        data.missing_fields.plans_selection?.length === 0 &&
        metadata?.planIds?.length > 0 &&
        qleDetailsRequiredToShowStep(data, featureFlags)
      )
    },
    previousStep: () => 'medical_plan',
    nextStep: (data, featureFlags) => stepAfterMedicalPlan(data, featureFlags),
    layout: 'full',
  },
  smart_budget: {
    component: SmartBudgetPage,
    canShow: (data, _metadata, featureFlags) => {
      return (
        (featureFlags?.showSmartBudget ?? false) &&
        !allowanceIsEmpty(data.allowance) &&
        data.missing_fields.plans_selection?.length === 0 &&
        qleDetailsRequiredToShowStep(data, featureFlags)
      )
    },
    isCompleted: (data) =>
      hasCompletedPlanSelectionPrerequisites(data) && hasSelectedAllPlans(data),
    previousStep: () => 'medical_plan',
    nextStep: (data, featureFlags) => stepAfterVisionPlan(data, featureFlags),
    layout: 'no_sidebar',
  },
  dental_plan: {
    component: DentalPlansPage,
    canShow: (data, _metadata, featureFlags) =>
      data.missing_fields.plans_selection?.length === 0 &&
      !data.restrictions?.dental_not_available &&
      qleDetailsRequiredToShowStep(data, featureFlags),
    isCompleted: (data) =>
      !data.restrictions?.dental_not_available && dentalPlanSelected(data),
    previousStep: () => 'medical_plan',
    nextStep: (data, featureFlags) => stepAfterDentalPlan(data, featureFlags),
    layout: 'sidebar',
  },
  vision_plan: {
    component: VisionPlansPage,
    canShow: (data, _metadata, featureFlags) =>
      data.missing_fields.plans_selection?.length === 0 &&
      !data.restrictions?.vision_not_available &&
      qleDetailsRequiredToShowStep(data, featureFlags),
    isCompleted: (data) =>
      !data.restrictions?.vision_not_available && visionPlanSelected(data),
    previousStep: (data) => stepBeforeVisionPlan(data),
    nextStep: (data, featureFlags) => stepAfterVisionPlan(data, featureFlags),
    layout: 'sidebar',
  },
  state_form: {
    component: StateFormPage,
    canShow: (data, _metadata, featureFlags) =>
      hasCompletedPlanSelectionPrerequisites(data) &&
      hasSelectedAllPlans(data) &&
      !!data.state_config &&
      qleDetailsRequiredToShowStep(data, featureFlags),
    isCompleted: (data) =>
      !!data.missing_fields.enrollment &&
      !data.missing_fields.enrollment?.includes('state_config'),
    previousStep: (data, enrollmentEventId) =>
      lastPlanSelectionStep(data, enrollmentEventId),
    nextStep: (data, featureFlags) => {
      if (data && isNewQleFlow(data, featureFlags?.newQleEnabled)) {
        if (shouldSkipQleDocumentUpload(data.enrollment_event)) {
          return 'renewal_confirm_enrollment'
        }
        return 'qle_document_upload'
      }
      if (data && isRenewal(data)) {
        return 'renewal_confirm_enrollment'
      }
      return 'finalize_enrollment'
    },
    layout: 'sidebar',
  },
  finalize_enrollment: {
    component: ConfirmAndEnrollPage,
    canShow: (data, _metadata, featureFlags) =>
      hasCompletedPlanSelectionPrerequisites(data) &&
      hasSelectedAllPlans(data) &&
      (!data.state_config || !memberIsMissingStateEnrollmentFields(data)) &&
      !isRenewalOrQleFlow(data, featureFlags?.newQleEnabled), // Renewal and QLE flows use renewal_confirm_enrollment instead
    isCompleted: (data) => enrollmentMissingFieldsAreEmpty(data),
    previousStep: (data, enrollmentEventId, featureFlags) =>
      stepBeforeFinalizeEnrollment(data, enrollmentEventId, featureFlags),
    nextStep: () => null, // terminal step
    layout: 'sidebar',
  },
  opted_out: {
    component: OptedOutPage,
    canShow: (data) =>
      !allowanceIsEmpty(data.allowance) || data.progress === 'opted_out',
    // This is a special case where we can't really determine what the previous step is supposed to be
    // We read from the localStorage to see what the previous page was (based on the navigation history)
    // or get the step from the data in case sessionStorage got deleted in the meantime
    previousStep: (data, enrollmentEventId, featureFlags) => {
      const storedHistory = enrollmentEventId
        ? getStoredHistory(enrollmentEventId)
        : []
      let previousStep = null
      if (storedHistory.length >= 2) {
        // 2 = at least one current step (which would be OptedOutPage, and at least one previous step)
        previousStep = storedHistory[storedHistory.length - 2]
        return previousStep as Step
      } else {
        // If we can't find previous step from the navigation history, we deduce it from the onboarding data
        return data ? getStepFromData(data, featureFlags) : null
      }
    },
    nextStep: () => null, // terminal step
    layout: 'full',
  },
  opted_out_from_subsidy_eligible: {
    component: OptedOutFromSubsidyEligiblePage,
    canShow: (data) => eligibleForSubsidy(data),
    previousStep: () => 'subsidy_eligible',
    nextStep: () => null, // terminal step
    layout: 'full',
  },
  subsidy_eligible: {
    component: SubsidyEligiblePage,
    canShow: (data, _metadata, featureFlags) =>
      eligibleForSubsidy(data) && qleDetailsRequiredToShowStep(data, featureFlags),
    previousStep: (data, _enrollmentEventId, featureFlags) => {
      if (data && isRenewalOrQleFlow(data, featureFlags?.newQleEnabled)) {
        return 'renewal_confirm_info'
      }
      return 'additional_details'
    },
    nextStep: (data, featureFlags) => {
      if (data && isRenewalOrQleFlow(data, featureFlags?.newQleEnabled)) {
        return 'renewal_show_summary'
      }
      return 'medical_plan'
    },
    layout: 'sidebar',
  },
  // Renewal specific steps (also used for QLE/special enrollment)
  renewal_confirm_info: {
    component: RenewalConfirmInfoPage,
    canShow: (data, _metadata, featureFlags) =>
      !!data.member &&
      isRenewalOrQleFlow(data, featureFlags?.newQleEnabled) &&
      qleDetailsRequiredToShowStep(data, featureFlags),
    previousStep: (data, _enrollmentEventId, featureFlags) => {
      if (data && isNewQleFlow(data, featureFlags?.newQleEnabled)) {
        return 'qle_details'
      }
      return null
    },
    nextStep: (data) => {
      if (data && phoneNumberNeedsToBeVerified(data)) {
        return 'phone_verification'
      }
      if (data && eligibleForSubsidy(data)) {
        return 'subsidy_eligible'
      }
      return 'renewal_show_summary'
    },
    layout: 'no_sidebar',
  },
  renewal_show_summary: {
    component: RenewalShowSummaryPage,
    canShow: (data, _metadata, featureFlags) =>
      isRenewalOrQleFlow(data, featureFlags?.newQleEnabled) &&
      qleDetailsRequiredToShowStep(data, featureFlags) &&
      memberAllowanceMissingFieldsAreEmpty(data) &&
      participantsAllowanceMissingFieldsAreEmpty(data) &&
      !allowanceIsEmpty(data.allowance) &&
      phoneNumberVerified(data) &&
      !memberMissingCounty(data),
    previousStep: (data) => {
      if (data && eligibleForSubsidy(data)) {
        return 'subsidy_eligible'
      }
      return 'renewal_confirm_info'
    },
    nextStep: (data, featureFlags) => {
      if (data?.state_config && memberIsMissingStateEnrollmentFields(data)) {
        return 'state_form'
      }

      // For QLE flows, check if document upload is needed before confirmation
      if (data && isNewQleFlow(data, featureFlags?.newQleEnabled)) {
        if (!shouldSkipQleDocumentUpload(data.enrollment_event)) {
          return 'qle_document_upload'
        }
      }
      return 'renewal_confirm_enrollment'
    },
    layout: 'no_sidebar',
  },
  renewal_confirm_enrollment: {
    component: ConfirmAndEnrollPage,
    canShow: (data, _metadata, featureFlags) =>
      isRenewalOrQleFlow(data, featureFlags?.newQleEnabled) &&
      (!isNewQleFlow(data, featureFlags?.newQleEnabled) || qleDetailsCompleted(data)) &&
      hasCompletedPlanSelectionPrerequisites(data) &&
      hasSelectedAllPlans(data) &&
      (!data.state_config || !memberIsMissingStateEnrollmentFields(data)) &&
      proofOfQleDocumentsStepCompletedIfQLEFlow(data, featureFlags),
    isCompleted: (data) =>
      enrollmentMissingFieldsAreEmpty(data) && data.progress === 'completed',
    previousStep: (data, enrollmentEventId, featureFlags) =>
      stepBeforeFinalizeEnrollment(data, enrollmentEventId, featureFlags),
    nextStep: () => null, // terminal step
    layout: 'sidebar',
  },
  renewal_curr_year_medical_plan_details: {
    component: RenewalCurrMedicalPlanDetailPage,
    canShow: (data, metadata, featureFlags) => {
      return (
        data.missing_fields.plans_selection?.length === 0 &&
        metadata?.planIds?.length > 0 &&
        isRenewalOrQleFlow(data, featureFlags?.newQleEnabled) &&
        qleDetailsRequiredToShowStep(data, featureFlags)
      )
    },
    previousStep: () => null,
    nextStep: (data, featureFlags) => stepAfterMedicalPlan(data, featureFlags),
    layout: 'full',
  },
  renewal_next_year_medical_plan_details: {
    component: RenewalNextMedicalPlanDetailPage,
    canShow: (data, metadata, featureFlags) => {
      return (
        data.missing_fields.plans_selection?.length === 0 &&
        metadata?.planIds?.length > 0 &&
        isRenewalOrQleFlow(data, featureFlags?.newQleEnabled) &&
        qleDetailsRequiredToShowStep(data, featureFlags)
      )
    },
    previousStep: () => null,
    nextStep: (data, featureFlags) => stepAfterMedicalPlan(data, featureFlags),
    layout: 'full',
  },
  qle_document_upload: {
    component: QleDocumentUploadPage,
    canShow: (data, _metadata, featureFlags) => {
      if (!isNewQleFlow(data, featureFlags?.newQleEnabled)) {
        return false
      }
      if (!data.member) {
        return false
      }
      return qleDetailsCompleted(data) &&
        hasCompletedPlanSelectionPrerequisites(data) &&
        hasSelectedAllPlans(data) &&
        (!data.state_config || !memberIsMissingStateEnrollmentFields(data)) &&
        !shouldSkipQleDocumentUpload(data.enrollment_event)
    },
    isCompleted: (data) => proofOfQleDocumentsUploaded(data),
    previousStep: (data, enrollmentEventId) => {
      // Go back to state_form if applicable, otherwise last plan selection step
      if (data?.state_config) {
        return 'state_form'
      }
      return lastPlanSelectionStep(data, enrollmentEventId)
    },
    nextStep: () => 'renewal_confirm_enrollment',
    layout: 'no_sidebar',
  },
}

/* Logic needed to decide if the request step can actually be shown */
export const canShowThisStep = ({
  data,
  step,
  featureFlags,
}: {
  data: OnboardingInfo;
  step: Step;
  featureFlags?: FeatureFlags;
}) => {
  return (
    componentMappings[getBaseStep(step)]?.canShow(
      data,
      getStepMetadata(step),
      featureFlags
    ) || false
  )
}

export const isThisStepCompleted = ({
  data,
  step,
  featureFlags,
}: {
  data: OnboardingInfo;
  step: Step;
  featureFlags?: FeatureFlags;
}) => {
  return componentMappings[getBaseStep(step)]?.isCompleted
    ? componentMappings[getBaseStep(step)]?.isCompleted?.(data, featureFlags)
    : canShowThisStep({ data, step, featureFlags })
}
